// Rug Room Shots: thin wrappers over apiCall for every endpoint in
// rug-room-shots-worker/SPEC.md section 3.4 (+ section 9.10: speed on Start and
// Regenerate). Every endpoint is gated on the backend (admins/owners or the "rug-room-shots" interface key;
// Pause/Resume too since the owner decision of 2026-10-12); a 403 surfaces as an Error with .status = 403. Paid requests (Start,
// Regenerate, bulk regenerate) can be refused for non-admins with 409 { code: "daily_limit", ... }.
// Errors from apiCall carry .status and .body (e.g. 409 { code, current }).
// The endpoints of the 2026-10-21 release (downloads, zip exports, detail photos, the Outdoor & sunroom list,
// room-type rules, re-plan) are derived from RRS_CONFIG here, so src/config/api.js stays unchanged (build spec 9.1).
import { API_ENDPOINTS as E, apiCall } from '../../config/api';
import { saveResponseAsFile, easternFileStamp } from './common';

const RRS_BASE = E.RRS_CONFIG.replace(/\/config$/, '');
const enc = (v) => encodeURIComponent(String(v));

const qs = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
};

const post = (url, body) => apiCall(url, { method: 'POST', body: JSON.stringify(body || {}) });
const put = (url, body) => apiCall(url, { method: 'PUT', body: JSON.stringify(body || {}) });
const del = (url) => apiCall(url, { method: 'DELETE' });

const rrsApi = {
  health: () => apiCall(E.RRS_HEALTH),
  config: () => apiCall(E.RRS_CONFIG),

  // Jobs
  createJob: ({ name, text, options }) => post(E.RRS_JOBS, { name: name || undefined, text, options }),
  uploadJob: ({ file, name, options }) => {
    const fd = new FormData();
    fd.append('file', file);
    if (name) fd.append('name', name);
    fd.append('options', JSON.stringify(options || {}));
    return apiCall(E.RRS_JOBS_UPLOAD, { method: 'POST', body: fd });
  },
  listJobs: (params) => apiCall(`${E.RRS_JOBS}${qs(params)}`),
  getJob: (jobId) => apiCall(E.RRS_JOB(jobId)),
  getTargets: (jobId, params) => apiCall(`${E.RRS_JOB_TARGETS(jobId)}${qs(params)}`),
  // speed: 'fast' | 'batch' (SPEC 9.10); expectedUsd is the total at that speed. acknowledgeOldPlan starts a plan
  // made before the 2026-10-21 rules anyway (otherwise 409 old_plan, build spec 4.11).
  startJob: (jobId, { expectedReadyCount, expectedUsd, speed, acknowledgeOldPlan }) => post(E.RRS_JOB_START(jobId), {
    expectedReadyCount, expectedUsd, speed: speed || undefined, acknowledgeOldPlan: acknowledgeOldPlan ? true : undefined,
  }),
  // Cancel a planned, never-started job and plan the same IDs again as a new job (201 { oldJobId, newJobId, ... }).
  replanJob: (jobId, note) => post(`${RRS_BASE}/jobs/${enc(jobId)}/replan`, { note: note || undefined }),
  cancelJob: (jobId, note) => post(E.RRS_JOB_CANCEL(jobId), { note: note || undefined }),
  exportJob: async (jobId) => {
    const res = await apiCall(E.RRS_JOB_EXPORT(jobId));
    if (res && typeof res.blob === 'function') {
      await saveResponseAsFile(res, `Rug Room Shots job ${jobId} ${easternFileStamp()}.csv`);
      return;
    }
    throw new Error('The export did not return a file.');
  },

  // Plan overrides
  setStyle: (targetId, { style, note, jobId }) => post(E.RRS_TARGET_STYLE(targetId), { style, note: note || undefined, jobId }),
  // confirmExisting: make a shot for a rug that already has a room photo (else 409 confirm_existing).
  setRoom: (targetId, { roomCode, note, jobId, confirmExisting }) => post(E.RRS_TARGET_ROOM(targetId), {
    roomCode, note: note || undefined, jobId, confirmExisting: confirmExisting ? true : undefined,
  }),
  setExcluded: (targetId, excluded) => post(E.RRS_TARGET_EXCLUDE(targetId), { excluded }),

  // Review. params: state, style, shape, reason (a reject reason), allAttempts ('true' | 'false'), page, pageSize,
  // and the AI check (SPEC_AICHECK 4.5): ai (fail | look | pass | not_pass | unchecked | error | checking),
  // aiBelow (1..100), aiAtLeast (0..100), sort (default | ai_worst). Empty values are dropped by qs.
  getReview: (jobId, params) => apiCall(`${E.RRS_JOB_REVIEW(jobId)}${qs(params)}`),
  getGeneration: (genId) => apiCall(E.RRS_GENERATION(genId)),
  reviewGeneration: (genId, { decision, reasonCode, note, correctStyle }) => post(E.RRS_GENERATION_REVIEW(genId), {
    decision,
    reasonCode: reasonCode || undefined,
    note: note || undefined,
    correctStyle: correctStyle || undefined,
  }),
  // detailMode: 'none' | 'default' (the rug's detail photo on the image server) | 'upload' (detailUploadKey).
  regenerate: (genId, {
    note, roomCode, noteInPrompt, speed, detailMode, detailUploadKey,
  }) => post(E.RRS_GENERATION_REGENERATE(genId), {
    note,
    roomCode: roomCode || null,
    noteInPrompt: !!noteInPrompt,
    speed: speed || undefined,
    detailMode: detailMode || 'none',
    detailUploadKey: detailMode === 'upload' ? detailUploadKey : undefined,
  }),
  // Bulk regenerate (SPEC 10). preview is read-only: { selection, roomMode, roomChoices, moveStyleChanged,
  // includeApproved, detailMode } -> { count, genIds, speeds[{code, estUsd, available}], skippedCounts, groups, ... }.
  // selection: { mode: 'ids', genIds } or { mode: 'filter', filter: { state, style, shape, reason, allAttempts } }
  // (the filter also takes the AI keys ai / aiBelow / aiAtLeast, SPEC_AICHECK 4.10; the preview then adds
  // aiNotes [{ genId, itemId, checkId, score, state, note }], aiNoteCount, aiNoteMissingCount).
  // The commit body adds
  // clientRef (one per dialog session: a repeat is refused with 409 already_submitted), genIds,
  // expectedCount / expectedUsd (re-checked in the transaction: 409 selection_changed), speed and note,
  // and (AI check) useAiNotes plus noteOverrides [{ genId, note }] for the AI notes the person edited.
  regeneratePreview: (jobId, body) => post(E.RRS_JOB_REGENERATE_PREVIEW(jobId), body),
  regenerateMany: (jobId, body) => post(E.RRS_JOB_REGENERATE(jobId), body),

  // AI check (SPEC_AICHECK 4.7-4.12). "Check with AI" for many images: preview (read-only) { selection: { mode:
  // 'unchecked' | 'ids' | 'filter', genIds?, filter? }, recheck, onlyPending } -> { count, genIds, usdPerCheck, estUsd,
  // byReviewState, skippedCounts, skipped, generationPaused, ... }; the commit adds clientRef, genIds, expectedCount
  // and expectedUsd (409 already_submitted / selection_changed / nothing_eligible / worker_not_ready / daily_limit).
  aiCheckPreview: (jobId, body) => post(`${RRS_BASE}/jobs/${enc(jobId)}/ai-checks/preview`, body),
  aiCheckQueue: (jobId, body) => post(`${RRS_BASE}/jobs/${enc(jobId)}/ai-checks`, body),
  // Stop the job's checks still waiting (running ones finish) -> { cancelled }.
  aiCheckCancel: (jobId) => post(`${RRS_BASE}/jobs/${enc(jobId)}/ai-checks/cancel`, {}),
  // One image, also a re-check -> { checkId, genId, status, estUsd, generationPaused } (409 checking / not_succeeded /
  // outputs_not_ready / worker_not_ready / daily_limit).
  aiCheckOne: (genId) => post(`${RRS_BASE}/generations/${enc(genId)}/ai-check`, {}),
  // Approve AI-passed images: preview (read-only) { selection, minScore } -> { count, genIds, scoreBands, sample,
  // skippedCounts, ... }; the commit adds clientRef, genIds and expectedCount.
  approvePreview: (jobId, body) => post(`${RRS_BASE}/jobs/${enc(jobId)}/approve/preview`, body),
  approveMany: (jobId, body) => post(`${RRS_BASE}/jobs/${enc(jobId)}/approve`, body),
  // The automatic-check switch and the Pass / Fail scores (GET everyone, PUT administrators / owners).
  getAiCheckSettings: () => apiCall(`${RRS_BASE}/settings/ai-check`),
  saveAiCheckSettings: (body) => put(`${RRS_BASE}/settings/ai-check`, body),

  // Rooms
  listRooms: (params) => apiCall(`${E.RRS_ROOMS}${qs(params)}`),
  roomMatrix: () => apiCall(E.RRS_ROOMS_MATRIX),
  // Least-used active room for a style x shape (backend rule), optionally excluding one. With targetId the
  // target's room letters filter the rooms (build spec 5.1).
  suggestRoom: (style, shape, exclude, targetId) => apiCall(`${E.RRS_ROOMS_SUGGEST}${qs({
    style, shape, exclude, targetId,
  })}`),
  // Ask the worker to re-scan safaviehimages for new coded ROOMAI rooms.
  requestRoomScan: () => post(E.RRS_ROOMS_SCAN, {}),
  roomCandidates: (roomCode) => apiCall(E.RRS_ROOM_CANDIDATES(roomCode)),
  setRoomStatus: (roomCode, { status, note }) => post(E.RRS_ROOM_STATUS(roomCode), { status, note: note || '' }),
  setRoomBase: (roomCode, imageKey) => post(E.RRS_ROOM_BASE(roomCode), { imageKey }),
  // Next free page-added room code for a prefix (a preview: POST /rooms returns the code actually saved).
  nextRoomCode: (style, type, letter) => apiCall(`${E.RRS_ROOMS_NEXT_CODE}${qs({ style, type, letter })}`),
  // Add a room from an uploaded photo (multipart). Created Proposed + pinned; activate with setRoomStatus.
  addRoom: ({ file, styleCode, typeCode, letter, note }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('styleCode', styleCode);
    fd.append('typeCode', typeCode);
    if (letter) fd.append('letter', letter);
    if (note) fd.append('note', note);
    return apiCall(E.RRS_ROOMS, { method: 'POST', body: fd });
  },

  // Styles: add a hand-picked style (insert-only; never renamed or removed).
  addStyle: ({ code, name, note }) => post(E.RRS_STYLES, { code, name, note: note || '' }),

  // Downloads (build spec 5.4). downloadFiles: one page of presigned links ({ selection: 'approved' | 'succeeded',
  // skipNamesOnImageServer, page, pageSize }). Zip exports are built by the worker; GET exports/{id} presigns its
  // parts fresh on every call, so the page asks for it only when a part's button is clicked.
  downloadFiles: (jobId, params) => apiCall(`${RRS_BASE}/jobs/${enc(jobId)}/downloads/files${qs(params)}`),
  createExport: (jobId, body) => post(`${RRS_BASE}/jobs/${enc(jobId)}/exports`, body),
  listExports: (jobId) => apiCall(`${RRS_BASE}/jobs/${enc(jobId)}/exports`),
  getExport: (exportId) => apiCall(`${RRS_BASE}/exports/${enc(exportId)}`),
  cancelExport: (exportId) => post(`${RRS_BASE}/exports/${enc(exportId)}/cancel`, {}),

  // Detail photos (build spec 5.3): the default detail photo of a target and the last upload for its WebID.
  targetDetail: (targetId) => apiCall(`${RRS_BASE}/targets/${enc(targetId)}/detail-photo`),
  uploadDetail: (targetId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiCall(`${RRS_BASE}/targets/${enc(targetId)}/detail-photo`, { method: 'POST', body: fd });
  },

  // Outdoor & sunroom list (build spec 5.5): settings rows scene:<PREFIX>.
  listScenes: () => apiCall(`${RRS_BASE}/scenes`),
  saveScene: (prefix, body) => put(`${RRS_BASE}/scenes/${enc(prefix)}`, body),
  removeScene: (prefix) => del(`${RRS_BASE}/scenes/${enc(prefix)}`),

  // Room-type rules (build spec 5.5). PUT replaces the stored object (keys left out fall back to the defaults).
  getRoomRules: () => apiCall(`${RRS_BASE}/room-rules`),
  saveRoomRules: (body) => put(`${RRS_BASE}/room-rules`, body),
  resetRoomRules: () => del(`${RRS_BASE}/room-rules`),

  // Half-price batches (read-only; SPEC 9.10)
  listBatches: (jobId) => apiCall(E.RRS_JOB_BATCHES(jobId)),
  getBatch: (batchId, limit) => apiCall(`${E.RRS_BATCH(batchId)}${qs({ limit })}`),

  // Spend & log
  spend: (month) => apiCall(`${E.RRS_SPEND}${qs({ month })}`),
  events: (params) => apiCall(`${E.RRS_EVENTS}${qs(params)}`),
  setPaused: (paused) => post(E.RRS_SETTINGS_PAUSE, { paused: !!paused }),
};

export default rrsApi;
